import type { components } from "@oathera/contract/api";
import type { Measure, StatusLevel } from "../../components/index.ts";

/**
 * Presentation vocabulary for the Identity MCP screen (task 7.3; requirement 13.3). It keeps the
 * one-way maps from contract shapes onto the screen's presentational pieces in a single, testable
 * place:
 *
 * - how a `Quantity`'s exactness becomes an exact/estimate/unverifiable `Measure` (design 12.3);
 * - the human labels for the `identity_mcp_tool`, `result` and `identity_mcp_reason` enums, each an
 *   exhaustive `Record` over the generated contract type;
 * - how a Protected_Core `core_state` becomes a status level (icon + text, never colour alone) and
 *   the `/integrity` link an operator follows to see the attestation detail;
 * - the honest reading of the opt-in coverage counts (dark = off on purpose, not broken).
 *
 * It defines no API shapes; those come from `@oathera/contract/api` via `use-identity-mcp.ts`.
 */

type Quantity = components["schemas"]["Quantity"];
type IdentityMcpTool = components["schemas"]["enum_identity_mcp_tool"];
type Result = components["schemas"]["enum_result"];
type IdentityMcpReason = components["schemas"]["enum_identity_mcp_reason"];
type CoreState = components["schemas"]["enum_core_state"];

/** Map a `Quantity`'s exactness onto the shared exact/estimate vocabulary (design 12.3). */
export function quantityMeasure(q: Quantity): Measure {
  // No data cannot be verified: neither an exact counter nor a sampled estimate, so never green.
  if (q.value === null) return "unverifiable";
  return q.exactness === "estimated" ? "estimate" : "exact";
}

/** Map a `Quantity.exactness` directly (used for series points that are always present). */
export function exactnessMeasure(exactness: components["schemas"]["Exactness"]): Measure {
  return exactness === "estimated" ? "estimate" : "exact";
}

/* ---- Human labels for the catalog enums ----------------------------------------------------- */

/*
 * Every label map below is an exhaustive `Record` over the generated contract enum (Requirement
 * 13.3). A catalog member added without a label fails the type check here, and the vocabulary DOM
 * test walks the generated runtime arrays, so a raw `snake_case` code is never shown as a label.
 * There is deliberately no fallback to the raw code.
 */

/** Human labels for every `identity_mcp_tool` member (contract 1.7 adds `get_passport`). */
export const TOOL_LABELS: Readonly<Record<IdentityMcpTool, string>> = {
  enroll: "Enroll",
  issue_token: "Issue token",
  status: "Status",
  sign_request: "Sign request",
  sign_operation: "Sign operation",
  get_passport: "Get Passport",
  other: "Other",
};

/** The human label for an Identity MCP tool. */
export function toolLabel(tool: IdentityMcpTool): string {
  return TOOL_LABELS[tool];
}

/** Human labels for every `result` member. */
export const RESULT_LABELS: Readonly<Record<Result, string>> = {
  success: "Success",
  failure: "Failure",
};

/** The human label for an operation outcome. */
export function resultLabel(outcome: Result): string {
  return RESULT_LABELS[outcome];
}

/**
 * Human labels for every `identity_mcp_reason` member (contract 1.7 adds the four `passport_*`
 * reasons). The raw code stays available beside the label as a monospace affordance.
 */
export const REASON_LABELS: Readonly<Record<IdentityMcpReason, string>> = {
  activation_verification_failed: "Activation verification failed",
  agent_suspended: "Agent suspended",
  ambiguous_binding: "Ambiguous binding",
  anchor_mismatch: "Anchor mismatch",
  approval_mismatch: "Approval mismatch",
  approval_replayed: "Approval replayed",
  approval_required: "Approval required",
  approval_store_unavailable: "Approval store unavailable",
  attribute_elicitation_cancel: "Attribute elicitation cancel",
  attribute_elicitation_decline: "Attribute elicitation decline",
  attribute_elicitation_failed: "Attribute elicitation failed",
  audience_expansion: "Audience expansion",
  bearer_auth_forbidden: "Bearer auth forbidden",
  bearer_invalid: "Bearer invalid",
  bearer_missing: "Bearer missing",
  binding_incomplete: "Binding incomplete",
  binding_required: "Binding required",
  capability_expansion: "Capability expansion",
  core_integrity_failed: "Core integrity failed",
  core_unavailable: "Core unavailable",
  delegation_cycle: "Delegation cycle",
  depth_exceeded: "Depth exceeded",
  expired_activation: "Expired activation",
  expired_approval: "Expired approval",
  expired_context: "Expired context",
  expired_delegation: "Expired delegation",
  expired_token: "Expired token",
  host_changed: "Host changed",
  insufficient_capability: "Insufficient capability",
  internal: "Internal",
  invalid_activation: "Invalid activation",
  invalid_allowlist: "Invalid allowlist",
  invalid_approval: "Invalid approval",
  invalid_audience: "Invalid audience",
  invalid_context: "Invalid context",
  invalid_credential_id: "Invalid credential ID",
  invalid_delegation: "Invalid delegation",
  invalid_operation: "Invalid operation",
  invalid_operation_arguments: "Invalid operation arguments",
  invalid_operation_proof: "Invalid operation proof",
  invalid_session_manager: "Invalid session manager",
  invalid_signature: "Invalid signature",
  invalid_tenant: "Invalid tenant",
  invalid_token: "Invalid token",
  invalid_token_binding: "Invalid token binding",
  issuance_failed: "Issuance failed",
  lifetime_expansion: "Lifetime expansion",
  mtls_client_unauthorized: "mTLS client unauthorized",
  no_session_key: "No session key",
  no_token: "No token",
  peer_credentials_unverified: "Peer credentials unverified",
  platform_identity_unverified: "Platform identity unverified",
  policy_denied: "Policy denied",
  rate_limited: "Rate limited",
  revoked: "Revoked",
  session_expired: "Session expired",
  sign_request_denied: "Sign request denied",
  sign_request_disabled: "Sign request disabled",
  subject_mismatch: "Subject mismatch",
  temporarily_unavailable: "Temporarily unavailable",
  tenant_mismatch: "Tenant mismatch",
  timeout: "Timeout",
  token_refresh_required: "Token refresh required",
  unauthenticated: "Unauthenticated",
  unknown_key: "Unknown key",
  unmapped_credential: "Unmapped credential",
  unsupported_auth_mode: "Unsupported auth mode",
  unsupported_version: "Unsupported version",
  untrusted_issuer: "Untrusted issuer",
  policy_missing: "Policy missing",
  policy_unavailable: "Policy unavailable",
  policy_unverifiable: "Policy unverifiable",
  policy_mismatch: "Policy mismatch",
  policy_expired: "Policy expired",
  policy_unsupported_version: "Policy unsupported version",
  policy_unsupported_control_member: "Policy unsupported control member",
  control_operation_denied: "Control operation denied",
  control_target_denied: "Control target denied",
  control_argument_denied: "Control argument denied",
  control_outside_time_window: "Control outside time window",
  control_idempotency_required: "Control idempotency required",
  control_approval_required: "Control approval required",
  control_task_context_required: "Control task context required",
  control_delegation_denied: "Control delegation denied",
  passport_unavailable: "Passport unavailable",
  passport_invalid: "Passport invalid",
  passport_revoked: "Passport revoked",
  passport_mismatch: "Passport mismatch",
  other: "Other",
};

/** The human label for a denial `reason` code. */
export function reasonLabel(reason: IdentityMcpReason): string {
  return REASON_LABELS[reason];
}

/* ---- Protected_Core state (linked to /integrity) -------------------------------------------- */

/**
 * Map a Protected_Core `core_state` onto a status level (icon + text, never colour alone; design
 * 12.4). `verified` is the only healthy state; `degraded` warns; `failed`/`absent` are errors; an
 * `other` or an unknown/`null` state is grey `unknown`, never green ("unverifiable is never green",
 * design 12.3).
 */
export function coreStateLevel(state: CoreState | null): StatusLevel {
  switch (state) {
    case "verified":
      return "healthy";
    case "degraded":
      return "warn";
    case "failed":
    case "absent":
      return "error";
    case "other":
    case null:
    case undefined:
      return "unknown";
  }
}

const CORE_STATE_LABELS: Readonly<Record<CoreState, string>> = {
  verified: "Verified",
  degraded: "Degraded",
  failed: "Failed",
  absent: "Absent",
  other: "Other",
};

/** The human label for a Protected_Core state; `null` reads "Unknown". */
export function coreStateLabel(state: CoreState | null): string {
  if (state === null || state === undefined) return "Unknown";
  return CORE_STATE_LABELS[state] ?? state;
}

/**
 * The `/integrity` link an operator follows from a Protected_Core row to see the full attestation
 * and checkpoint history. The Integrity screen is the authoritative view of Protected_Core state;
 * this screen only surfaces the latest per-instance state and hands off there (requirement 13.3
 * "Protected_Core status per instance linked to `/integrity`"). The link is a plain relative route.
 */
export function integrityHref(): string {
  return "/integrity";
}
